/**
 * Manages individual item search results and their
 * addition to the review form. Handles the creation of
 * new review items from search results while maintaining
 * proper form state and preventing duplicates.
 *
 * Supports different venue types via configurable
 * resultsContainerId value.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    id: String,
    name: String,
    type: String,
    resultsContainerId: {
      type: String,
      default: "review_items_search_results",
    },
  };

  /**
   * Adds an item to the review form.
   * Handles focus management, duplicate prevention,
   * and proper form state updates.
   * @param {Event} event - The click event
   */
  addToReview(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = document.querySelector(
      "form[data-review-form]"
    );
    if (!form) return;

    if (this.isItemAlreadyAdded()) {
      return;
    }

    // Store scroll position to maintain it after
    // adding the item
    const modalBody = document.getElementById(
      "modal-scrollable-body"
    );
    const previousScrollTop = modalBody?.scrollTop;

    const reviewItem = this.createReviewItem();
    const searchResults = document.getElementById(
      this.resultsContainerIdValue
    );

    if (searchResults) {
      searchResults.after(reviewItem);
    }

    this.element.remove();

    // Update search results container state
    if (searchResults) {
      const remainingItems =
        searchResults.querySelectorAll(
          '[data-controller="review-item-search-result"]'
        );
      if (remainingItems.length === 0) {
        searchResults.classList.remove("expanded");
      }
    }

    // Restore scroll position for smooth UX
    if (modalBody && previousScrollTop !== undefined) {
      modalBody.scrollTop = previousScrollTop;
    }
  }

  /**
   * Checks if this item is already added to the
   * review form.
   * @returns {boolean} Whether the item is already
   *   added
   */
  isItemAlreadyAdded() {
    const existingItems = document.querySelectorAll(
      "[data-review-target='item']"
    );
    return Array.from(existingItems).some((item) => {
      const idInput =
        item.querySelector("[data-reviewable-id]");
      const typeInput =
        item.querySelector("[data-reviewable-type]");
      return (
        idInput?.value === this.idValue &&
        typeInput?.value === this.typeValue
      );
    });
  }

  /**
   * Creates a new review item from the template.
   * Sets up all necessary form fields and attributes.
   * @returns {HTMLElement} The created review item
   */
  createReviewItem() {
    const template = document.querySelector(
      "[data-review-item-template]"
    );
    if (!template) return;

    const clone = template.content.cloneNode(true);
    const reviewItem = clone.querySelector(
      "[data-review-target='item']"
    );

    // Set up the polymorphic association fields
    const typeInput =
      reviewItem.querySelector("[data-reviewable-type]");
    const idInput =
      reviewItem.querySelector("[data-reviewable-id]");
    const nameDisplay =
      reviewItem.querySelector("[data-reviewable-name]");

    if (typeInput) typeInput.value = this.typeValue;
    if (idInput) idInput.value = this.idValue;
    if (nameDisplay) {
      nameDisplay.textContent = this.nameValue;
    }

    // Calculate the next index for the form fields
    const currentItems = document.querySelectorAll(
      "[data-review-target='item']"
    );
    const nextIndex = currentItems.length + 1;

    // Update all input names to use the sequential
    // index
    const inputs =
      reviewItem.querySelectorAll("input, textarea");
    inputs.forEach((input) => {
      const name = input.name;
      if (name) {
        input.name = name.replace(
          /\[items_attributes\]\[\d+\]/,
          `[items_attributes][${nextIndex}]`
        );
      }
    });

    return reviewItem;
  }
}
